import { type FC, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageTitle } from "../../../../components/PageTitle";
import { Button } from "../../../../components/ui/button";
import {
  useDeleteGroupApi,
  useGroupsApi,
} from "../../../../hooks/use-distribution-api";

export const GroupsPage: FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const { data, loading, error, refetch } = useGroupsApi({
    page,
    limit: 20,
    search: search || undefined,
  });
  const { deleteGroup } = useDeleteGroupApi();

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleDelete = async (groupId: string) => {
    setActionLoading(true);
    try {
      await deleteGroup(groupId);
      setDeleteConfirm(null);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const totalPages = data ? Math.ceil(data.total / 20) : 1;

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>配信グループ管理</PageTitle>
        <div className="alert alert-error">
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>配信グループ管理</PageTitle>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/distribution/contacts")}
          >
            連絡先管理
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => navigate("/admin/distribution/groups/new")}
          >
            + 新規作成
          </Button>
        </div>
      </div>

      {/* 検索 */}
      <div className="mb-6 flex gap-2">
        <input
          type="text"
          placeholder="グループ名で検索..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSearch()}
          className="input input-bordered w-full md:w-96"
        />
        <Button type="button" onClick={handleSearch}>
          検索
        </Button>
      </div>

      {/* テーブル */}
      <div className="card bg-base-100 shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>グループ名</th>
                <th>連絡先数</th>
                <th>作成者</th>
                <th>作成日</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-8">
                    <span className="loading loading-spinner loading-md" />
                  </td>
                </tr>
              ) : data?.items.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="text-center py-8 text-base-content/60"
                  >
                    グループがありません
                  </td>
                </tr>
              ) : (
                data?.items.map(group => (
                  <tr key={group.id} className="hover">
                    <td>
                      <Link
                        to={`/admin/distribution/groups/${group.id}`}
                        className="link link-primary font-medium"
                      >
                        {group.isDefault && (
                          <span className="badge badge-warning badge-sm mr-2">
                            デフォルト
                          </span>
                        )}
                        {group.name}
                      </Link>
                      {group.description && (
                        <div className="text-sm text-base-content/60 mt-1">
                          {group.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge badge-ghost">
                        {group.contactCount}
                      </span>
                    </td>
                    <td>{group.createdBy.name}</td>
                    <td>
                      {new Date(group.createdAt).toLocaleDateString("ja-JP")}
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="xs"
                          onClick={() =>
                            navigate(
                              `/admin/distribution/groups/${group.id}/edit`,
                            )
                          }
                        >
                          編集
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setDeleteConfirm(group.id)}
                          variant="ghost"
                          size="xs"
                          className="text-error"
                        >
                          削除
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ページネーション */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <div className="join">
            <Button
              type="button"
              size="sm"
              className="join-item"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              «
            </Button>
            <Button type="button" size="sm" className="join-item">
              {page} / {totalPages}
            </Button>
            <Button
              type="button"
              size="sm"
              className="join-item"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              »
            </Button>
          </div>
        </div>
      )}

      {/* 削除確認モーダル */}
      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">グループを削除しますか？</h3>
            <p className="py-4">
              この操作は取り消せません。グループ内の連絡先との紐付けも削除されます。
            </p>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDeleteConfirm(null)}
                disabled={actionLoading}
              >
                キャンセル
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  "削除"
                )}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !actionLoading && setDeleteConfirm(null)}
          />
        </div>
      )}
    </div>
  );
};
